/**
 * Court Mode — minimal fast interface for barristers on a court day.
 *
 * Shows only what matters at 9am in the robing room:
 * today's listed hearings, linked documents, quick notes, one-tap outcome.
 *
 * Design rule: no charts, no stat bars, no sidebar content. Speed first.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Gavel,
  MapPin,
  Clock,
  FileText,
  ArrowSquareOut,
  CheckCircle,
  Warning,
  ArrowLeft,
  Scales,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuthStore } from '@/features/auth/store';
import { canRecordHearingOutcome } from '@/lib/pupilUiBoundary';
import { useHearingsStore, type Hearing } from '@/lib/hearingsStore';
import { fetchDocuments } from '@/lib/documentsApi';
import { PostHearingModal } from '@/components/shared/PostHearingModal';
import { MatterCourtSituation } from '@/components/shared/MatterCourtSituation';
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { todayKey as getTodayKey } from '@/lib/diaryStore';
import { onCalendarUpdate, useWebSocketStore } from '@/lib/websocket';

export default function CourtModePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const fullName = user ? `${user.firstName} ${user.lastName}` : '';

  const allHearings = useHearingsStore((s) => s.hearings);
  const fetchHearings = useHearingsStore((s) => s.fetchHearings);
  const updateHearing = useHearingsStore((s) => s.updateHearing);
  const hearingsLoading = useHearingsStore((s) => s.loading);
  const hearingsError = useHearingsStore((s) => s.error);
  const wsConnected = useWebSocketStore((s) => s.connected);
  useEffect(() => { fetchHearings(); }, [fetchHearings]);
  useEffect(() => {
    const unsub = onCalendarUpdate(() => { fetchHearings(); });
    return unsub;
  }, [wsConnected, fetchHearings]);

  const todayKey = getTodayKey();

  const todayHearings = allHearings
    .filter(
      (h) =>
        h.date === todayKey &&
        (h.leadBarristerUserId === user?.id || h.juniorBarristerUserId === user?.id) &&
        !['vacated', 'completed'].includes(h.status),
    )
    .sort((a, b) => a.time.localeCompare(b.time));

  // Court Day is read in the robing room, on a phone, minutes before a listing. "No hearings
  // listed for today" must therefore mean the court list is genuinely empty — never that the
  // request failed, and never that it has not answered yet. The store already records both
  // (lib/hearingsStore.ts: `loading`, `error`); this screen simply never read them.
  const firstLoadPending = hearingsLoading && allHearings.length === 0;
  const loadFailed = !!hearingsError && allHearings.length === 0;

  const [outcomeTarget, setOutcomeTarget] = useState<Hearing | null>(null);
  const [expandedDocs, setExpandedDocs] = useState<string | null>(null); // matterId
  const [previewDoc, setPreviewDoc] = useState<{ id: string; name: string } | null>(null);

  return (
    <div className="mx-auto max-w-2xl space-y-5 px-4 py-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/barrister')}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">Court Day</h1>
          <p className="text-xs text-muted-foreground">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* The court list could not be fetched — said plainly, with a way to retry, instead of
          falling through to "no hearings listed" */}
      {loadFailed && (
        <Card className="flex flex-col items-center justify-center py-10 text-center">
          <QueryErrorState what="today's court list" onRetry={() => void fetchHearings()} />
          <Button variant="outline" size="sm" onClick={() => navigate('/barrister/hearings')}>
            View all hearings
          </Button>
        </Card>
      )}

      {/* First load still in flight */}
      {firstLoadPending && !loadFailed && (
        <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <Scales className="h-10 w-10 animate-pulse text-muted-foreground/30" weight="duotone" />
          <div className="text-sm text-muted-foreground">Loading today's court list…</div>
        </Card>
      )}

      {/* A refetch failed but the earlier list is still on screen: show it, and say it may be stale */}
      {hearingsError && allHearings.length > 0 && (
        <div role="alert" className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          <span>Couldn't refresh the court list — what you see below may be out of date.</span>
          <Button variant="outline" size="sm" onClick={() => void fetchHearings()}>
            Try again
          </Button>
        </div>
      )}

      {/* No hearings today */}
      {todayHearings.length === 0 && !loadFailed && !firstLoadPending && (
        <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <Scales className="h-10 w-10 text-muted-foreground/30" weight="duotone" />
          <div className="text-sm font-semibold text-foreground">No hearings listed for today</div>
          <p className="text-xs text-muted-foreground">Check the hearings list if you were expecting a listing.</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/barrister/hearings')}>
            View all hearings
          </Button>
        </Card>
      )}

      {/* Hearing cards */}
      {todayHearings.map((h) => (
        <HearingCard
          key={h.id}
          hearing={h}
          expandedDocs={expandedDocs}
          onExpandDocs={(matterId) => setExpandedDocs(expandedDocs === matterId ? null : matterId)}
          onOutcome={canRecordHearingOutcome(user?.memberType) ? () => setOutcomeTarget(h) : undefined}
          onNavigate={(id) => navigate(`/barrister/hearings/${id}`)}
          onPreview={(id, name) => setPreviewDoc({ id, name })}
        />
      ))}

      {/* In-app document preview — same modal the Documents page uses (PDF / DOCX / image),
          so counsel can read a bundle in court without leaving the day view. */}
      <DocumentPreviewModal
        documentId={previewDoc?.id ?? null}
        documentName={previewDoc?.name}
        onClose={() => setPreviewDoc(null)}
      />

      {/* Post-hearing modal — re-mounts per hearing via key */}
      {canRecordHearingOutcome(user?.memberType) && <PostHearingModal
        key={outcomeTarget?.id ?? 'none'}
        open={!!outcomeTarget}
        hearing={outcomeTarget}
        clerkName={fullName}
        onClose={() => setOutcomeTarget(null)}
        onSave={async (update) => {
          const saved = await updateHearing(update.hearingId, {
            status: update.result === 'adjourned' || update.result === 'part_heard' ? 'adjourned' : 'completed',
            outcome: update.outcome,
            outcomeType: update.outcomeType,
            judgmentReserved: update.judgmentReserved,
            costsOrdered: update.costsOrdered,
            orders: update.orders,
            counselNotes: update.counselNotes,
            nextHearingDate: update.nextHearingDate,
            costsPosition: update.costsPosition,
            deadlines: update.deadlines,
          });
          if (!saved) return false; // rolled back; the store has told the user why — modal stays open
          // Matter status is now a backend consequence of the outcome (conservative:
          // only clean disposals close) — no fragile frontend status jump here.
          qc.invalidateQueries({ queryKey: ['portal', 'threads'] });
          qc.invalidateQueries({ queryKey: ['matters'] });
          setOutcomeTarget(null);
          toast.success('Outcome recorded — solicitor has been notified');
          return true;
        }}
      />}
    </div>
  );
}

// ── Hearing card ─────────────────────────────────────────────

function HearingCard({
  hearing: h,
  expandedDocs,
  onExpandDocs,
  onOutcome,
  onNavigate,
  onPreview,
}: {
  hearing: Hearing;
  expandedDocs: string | null;
  onExpandDocs: (matterId: string) => void;
  /** Omit to draw no outcome control — a pupil may work the court day, never record the result. */
  onOutcome?: () => void;
  onPreview: (documentId: string, documentName: string) => void;
  onNavigate: (id: string) => void;
}) {
  const isCompleted = h.status === 'completed';
  const docsOpen = expandedDocs === h.matterId;

  const docsQ = useQuery({
    queryKey: ['court-mode-docs', h.matterId],
    queryFn: () => fetchDocuments(h.matterId),
    enabled: docsOpen,
    staleTime: 120_000,
  });

  const statusColour =
    h.status === 'completed'
      ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30'
      : h.status === 'in_progress'
      ? 'bg-brand-50 text-brand-700 dark:text-brand-300 border-brand-200'
      : 'bg-muted text-muted-foreground border-border';

  return (
    <div
      className={`overflow-hidden rounded-2xl border shadow-sm transition ${
        isCompleted ? 'border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/30 dark:bg-emerald-500/10' : 'border-border bg-card'
      }`}
    >
      {/* Time strip */}
      <div className={`flex items-center justify-between px-5 py-3 ${isCompleted ? 'bg-emerald-100/60 dark:bg-emerald-500/15' : 'bg-muted/30'}`}>
        <div className="flex items-center gap-3">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-lg font-bold text-foreground">{h.time}</span>
          <span className="text-sm text-muted-foreground">{h.type}</span>
        </div>
        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusColour}`}>
          {h.status.replace('_', ' ')}
        </span>
      </div>

      {/* Matter info */}
      <div className="px-5 py-4">
        <div className="text-base font-semibold leading-snug text-foreground">{h.matterName}</div>
        <div className="mt-0.5 text-xs text-brand-600 dark:text-brand-400">{h.matterNumber}</div>

        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-start gap-1.5 text-muted-foreground">
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{h.court}{h.courtroom ? ` · Room ${h.courtroom}` : ''}</span>
          </div>
          {h.judge && (
            <div className="flex items-start gap-1.5 text-muted-foreground">
              <Gavel className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{h.judge}</span>
            </div>
          )}
        </div>

        {/* Outcome strip if recorded */}
        {isCompleted && h.outcome && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2.5 text-xs">
            <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" weight="fill" />
            <span className="text-emerald-800 dark:text-emerald-300">{h.outcome}</span>
          </div>
        )}

        {/* Pending outcome warning */}
        {!isCompleted && h.date < getTodayKey() && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            <Warning className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />
            Outcome not yet recorded — please update
          </div>
        )}
      </div>

      {/* Prep checklist — the barrister's own preparation list for this hearing,
          the same panel shown on the matter page. Hidden once the hearing is done. */}
      {!isCompleted && (
        <div className="border-t border-border px-5 py-4">
          <MatterCourtSituation matterId={h.matterId} />
        </div>
      )}

      {/* Documents panel */}
      {docsOpen && (
        <div className="border-t border-border px-5 pb-4 pt-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Matter documents
          </div>
          {docsQ.isLoading && (
            <p className="text-xs text-muted-foreground">Loading…</p>
          )}
          {docsQ.data && docsQ.data.length === 0 && (
            <p className="text-xs text-muted-foreground">No documents uploaded for this matter yet.</p>
          )}
          <div className="space-y-1.5">
            {(docsQ.data ?? []).slice(0, 8).map((doc) => (
              <div
                key={doc.id}
                role="button"
                tabIndex={0}
                onClick={() => onPreview(doc.id, doc.name)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onPreview(doc.id, doc.name);
                  }
                }}
                title="Preview document"
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1 transition hover:bg-muted/40"
              >
                <FileText className="h-4 w-4 shrink-0 text-brand-500" weight="duotone" />
                <span className="flex-1 truncate text-xs text-foreground">{doc.name}</span>
                <span className="shrink-0 text-[10px] text-muted-foreground">{doc.kind}</span>
                {doc.downloadUrl && (
                  <a
                    href={doc.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    title="Open in new tab"
                    className="shrink-0 text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                  >
                    <ArrowSquareOut className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            ))}
          </div>
          {(docsQ.data?.length ?? 0) > 8 && (
            <button
              type="button"
              onClick={() => onNavigate(h.matterId)}
              className="mt-2 text-xs text-brand-600 dark:text-brand-400 hover:underline"
            >
              View all {docsQ.data!.length} documents →
            </button>
          )}
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-2 border-t border-border bg-muted/20 px-5 py-3">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<FileText className="h-3.5 w-3.5" />}
          onClick={() => onExpandDocs(h.matterId)}
        >
          {docsOpen ? 'Hide docs' : 'Documents'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowSquareOut className="h-3.5 w-3.5" />}
          onClick={() => onNavigate(h.id)}
        >
          Full detail
        </Button>
        <div className="ml-auto">
          {isCompleted ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
              <CheckCircle className="h-4 w-4" weight="fill" />
              Done
            </span>
          ) : (
            onOutcome && (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Gavel className="h-3.5 w-3.5" />}
              onClick={onOutcome}
            >
              Record outcome
            </Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
